/**
 * Drag geometry for the 3D designer's handles — pure, in surface-local feet.
 *
 * A drag is described by which handle is held and how far the pointer has moved
 * (already converted from screen pixels to feet). From that this module works
 * out the rectangle the user is asking for: move or resize, snap the moving
 * edges to nearby edges, keep SlatWall to whole rows, and keep the result on
 * the surface. Whether the result is *allowed* (overlaps etc.) is validation's
 * call — see `resolveDrag`, which combines the two.
 *
 * Handle names are SCREEN positions ('nw' = top-left of the rectangle as drawn).
 * A surface can be drawn mirrored — the left and right walls are viewed from
 * inside the room, so surface +x runs screen-LEFT — which is why the caller
 * says whether each axis is flipped.
 */

import { Surface, SectionProductType } from '../../types/sections';
import { SLATWALL_COVERAGE_HEIGHT } from '../constants';
import { Rect } from './resolver';
import { checkPlacement, MIN_SECTION_SIZE_FT, PlacementIssue, PlacementOptions } from './validation';

export type Handle = 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w' | 'move';

export interface SnapTargets {
  xs: number[];
  ys: number[];
}

export interface DragFrame {
  /** Surface +x runs toward screen-left (left / right walls). */
  xFlipped: boolean;
  /** Surface +y runs toward screen-bottom. */
  yFlipped: boolean;
  /** Snap distance in feet (the caller converts its pixel tolerance). */
  snapTolFt: number;
  targets: SnapTargets;
}

/** Which surface-space edges a handle moves, given how the surface is drawn. */
export function handleEdges(handle: Handle, frame: Pick<DragFrame, 'xFlipped' | 'yFlipped'>) {
  const move = handle === 'move';
  const screenLeft = handle.includes('w');
  const screenRight = handle.includes('e');
  const screenTop = handle.includes('n');
  const screenBottom = handle.includes('s');
  return {
    left: move || (frame.xFlipped ? screenRight : screenLeft),
    right: move || (frame.xFlipped ? screenLeft : screenRight),
    bottom: move || (frame.yFlipped ? screenTop : screenBottom),
    top: move || (frame.yFlipped ? screenBottom : screenTop),
  };
}

/**
 * Edges to snap to: the surface perimeter plus the edges of everything else on
 * it — sections and openings. `inset` adds lines that far in from the perimeter
 * (the openings' safe margin).
 */
export function snapTargetsFor(surface: Surface, excludeId?: string, inset = 0): SnapTargets {
  const xs = [0, surface.width];
  const ys = [0, surface.height];
  if (inset > 0) {
    xs.push(inset, surface.width - inset);
    ys.push(inset, surface.height - inset);
  }
  for (const s of [...surface.sections, ...(surface.openings ?? [])]) {
    if (s.id === excludeId) continue;
    xs.push(s.x, s.x + s.width);
    ys.push(s.y, s.y + s.height);
  }
  return { xs, ys };
}

/** The nearest target within `tol`, or the value unchanged. */
export function snapValue(value: number, targets: number[], tol: number): number {
  let best = value;
  let bestDist = tol;
  for (const t of targets) {
    const d = Math.abs(value - t);
    if (d < bestDist) { bestDist = d; best = t; }
  }
  return best;
}

/**
 * The rectangle a drag asks for, before any rule is applied: the held edges
 * follow the pointer and snap; a move keeps the section's size (it shifts by
 * the largest snap correction on each axis rather than stretching).
 */
export function computeCandidate(handle: Handle, orig: Rect, dxFt: number, dyFt: number, frame: DragFrame): Rect {
  const edges = handleEdges(handle, frame);
  let left = orig.x;
  let right = orig.x + orig.width;
  let bottom = orig.y;
  let top = orig.y + orig.height;

  if (edges.left) left += dxFt;
  if (edges.right) right += dxFt;
  if (edges.bottom) bottom += dyFt;
  if (edges.top) top += dyFt;

  const { xs, ys } = frame.targets;
  const tol = frame.snapTolFt;
  const sLeft = edges.left ? snapValue(left, xs, tol) : left;
  const sRight = edges.right ? snapValue(right, xs, tol) : right;
  const sBottom = edges.bottom ? snapValue(bottom, ys, tol) : bottom;
  const sTop = edges.top ? snapValue(top, ys, tol) : top;

  if (handle === 'move') {
    const pick = (a: number, b: number) => (Math.abs(a) > Math.abs(b) ? a : b);
    const dx = pick(sLeft - left, sRight - right);
    const dy = pick(sBottom - bottom, sTop - top);
    return { x: left + dx, y: bottom + dy, width: orig.width, height: orig.height };
  }
  return { x: sLeft, y: sBottom, width: sRight - sLeft, height: sTop - sBottom };
}

/**
 * SlatWall can only be whole 6" rows tall (the panels can't be ripped). Rounds
 * the height to whole rows, keeping the edge the user is NOT dragging in place.
 */
export function snapSlatWallHeight(rect: Rect, handle: Handle, frame: Pick<DragFrame, 'xFlipped' | 'yFlipped'>): Rect {
  const rows = Math.max(1, Math.round(rect.height / SLATWALL_COVERAGE_HEIGHT));
  const height = rows * SLATWALL_COVERAGE_HEIGHT;
  if (Math.abs(height - rect.height) < 1e-6) return rect;
  const edges = handleEdges(handle, frame);
  if (handle === 'move' || (!edges.top && !edges.bottom)) return rect; // height isn't changing
  if (edges.bottom) return { ...rect, height, y: rect.y + rect.height - height }; // top stays put
  return { ...rect, height }; // bottom stays put
}

/**
 * Keep a rectangle on its surface. A move keeps its size and is pushed back
 * inside; a resize is clipped at the edge. Null when it cannot fit at all.
 */
export function fitToSurface(rect: Rect, surface: Surface, isMove: boolean): Rect | null {
  let { x, y, width, height } = rect;
  if (isMove) {
    if (width > surface.width + 1e-9 || height > surface.height + 1e-9) return null;
    x = Math.min(Math.max(x, 0), surface.width - width);
    y = Math.min(Math.max(y, 0), surface.height - height);
  } else {
    if (x < 0) { width += x; x = 0; }
    if (y < 0) { height += y; y = 0; }
    if (x + width > surface.width) width = surface.width - x;
    if (y + height > surface.height) height = surface.height - y;
  }
  if (width < MIN_SECTION_SIZE_FT || height < MIN_SECTION_SIZE_FT) return null;
  return { x, y, width, height };
}

export type DragResult = { ok: true; rect: Rect } | { ok: false; issue: PlacementIssue };

/**
 * One step of a drag, start to finish: candidate → SlatWall rows → on the
 * surface → allowed? The overlay calls this on every pointer move and keeps the
 * last accepted rectangle, so a drag simply stops at anything it may not cross.
 */
export function resolveDrag(
  surface: Surface,
  section: { id: string; productType: SectionProductType },
  handle: Handle,
  orig: Rect,
  dxFt: number,
  dyFt: number,
  frame: DragFrame,
  options: Omit<PlacementOptions, 'ignoreId'> = {},
): DragResult {
  let rect = computeCandidate(handle, orig, dxFt, dyFt, frame);
  if (section.productType === 'SlatWall') rect = snapSlatWallHeight(rect, handle, frame);
  const fitted = fitToSurface(rect, surface, handle === 'move');
  if (!fitted) return { ok: false, issue: 'too-small' };
  let result = fitted;
  // Clipping a resize at the surface edge can leave SlatWall a part-row tall.
  if (section.productType === 'SlatWall' && handle !== 'move') {
    const rows = Math.floor(result.height / SLATWALL_COVERAGE_HEIGHT + 1e-6);
    if (rows < 1) return { ok: false, issue: 'too-small' };
    const height = rows * SLATWALL_COVERAGE_HEIGHT;
    if (Math.abs(height - result.height) > 1e-6) {
      const edges = handleEdges(handle, frame);
      result = edges.bottom && !edges.top
        ? { ...result, y: result.y + result.height - height, height }
        : { ...result, height };
    }
  }
  const issue = checkPlacement(surface, result, section.productType, { ...options, ignoreId: section.id });
  return issue ? { ok: false, issue } : { ok: true, rect: result };
}
